import React, { useState, useEffect, useRef } from 'react';
import { apiService, BlogPost } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { Plus, Edit, Trash2, Calendar, Tag, User as UserIcon, Globe, Search } from 'lucide-react';
import SafeJoditEditor from '../components/SafeJoditEditor';
import { editorConfig } from '../utils/joditConfig';
import { useToast } from '../contexts/ToastContext';

const Blog: React.FC = () => {
  const { showToast } = useToast();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState<any>(null);
  const [content, setContent] = useState('');
  const [activeTab, setActiveTab] = useState<'content' | 'seo'>('content');
  const editor = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [postData, catData] = await Promise.all([
        apiService.getBlogPosts(),
        apiService.getBlogCategories()
      ]);
      setPosts(postData || []);
      setCategories(catData || []);
    } catch (error) {
      console.error('Failed to fetch blog data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    formData.set('content', content);

    // Rename category to category_id for API compatibility
    const catId = formData.get('category');
    if (catId) {
      formData.set('category_id', catId);
      formData.delete('category');
    }

    // Strictly parse schema_markup and remove if invalid or empty
    const schemaMarkup = formData.get('schema_markup');
    if (schemaMarkup && typeof schemaMarkup === 'string' && schemaMarkup.trim()) {
      try {
        JSON.parse(schemaMarkup);
      } catch (e) {
        console.error('Invalid JSON in schema_markup');
        formData.delete('schema_markup');
      }
    } else {
      formData.delete('schema_markup');
    }

    // Handle empty files during PATCH to prevent validation errors
    if (editingPost) {
      const featuredImage = formData.get('featured_image') as File;
      if (featuredImage && featuredImage.size === 0) {
        formData.delete('featured_image');
      }
      const ogImage = formData.get('og_image') as File;
      if (ogImage && ogImage.size === 0) {
        formData.delete('og_image');
      }
    }

    // Cast read_time to integer
    const readTime = formData.get('read_time');
    if (readTime) {
      formData.set('read_time', Math.max(1, parseInt(readTime as string) || 5).toString());
    }

    // Normalize booleans
    if (formData.has('is_featured')) {
      const val = formData.get('is_featured');
      formData.set('is_featured', (val === 'on' || val === 'true') ? 'true' : 'false');
    } else {
      formData.append('is_featured', 'false');
    }

    // ROBUSTNESS: Remove empty strings from optional fields to prevent 500 errors
    const optionalFields = ['meta_title', 'meta_description', 'meta_keywords', 'canonical_url', 'robots_meta', 'og_title', 'og_description', 'focus_keyword', 'breadcrumbs'];
    optionalFields.forEach(field => {
      const val = formData.get(field);
      if (typeof val === 'string' && !val.trim()) {
        formData.delete(field);
      }
    });

    try {
      if (editingPost) {
        await apiService.updateBlogPost(editingPost.slug, formData);
      } else {
        await apiService.createBlogPost(formData);
      }

      setShowForm(false);
      setEditingPost(null);
      showToast('Blog post saved successfully!', 'success');
      fetchData();
    } catch (error) {
      console.error('Failed to save post:', error);
      showToast('Failed to save blog post. (Server Error 500)', 'error');
    }
  };

  const handleDelete = async (slug: string) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await apiService.deleteBlogPost(slug);
        showToast('Blog post deleted successfully!', 'success');
        fetchData();
      } catch (error) {
        console.error('Failed to delete post:', error);
      }
    }
  };

  const handleStatusToggle = async (post: BlogPost) => {
    try {
      if (post.status === 'published') {
        await apiService.unpublishBlogPost(post.slug);
        showToast('Blog post unpublished.', 'success');
      } else {
        await apiService.publishBlogPost(post.slug);
        showToast('Blog post published!', 'success');
      }
      fetchData();
    } catch (error) {
      console.error('Failed to toggle status:', error);
      showToast('Error updating status.', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Blog Posts</h1>
          <p className="text-gray-600 mt-1">Manage articles and news for your platform</p>
        </div>
        <button
          onClick={() => { setEditingPost(null); setContent(''); setActiveTab('content'); setShowForm(true); }}
          className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New Post
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {editingPost ? `Edit: ${editingPost.title}` : 'New Article'}
                </h3>
                <div className="flex mt-2 space-x-6">
                  <button type="button" onClick={() => setActiveTab('content')} className={`text-xs font-bold uppercase tracking-widest pb-1 border-b-2 transition ${activeTab === 'content' ? 'border-orange-600 text-orange-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>Content</button>
                  <button type="button" onClick={() => setActiveTab('seo')} className={`text-xs font-bold uppercase tracking-widest pb-1 border-b-2 transition ${activeTab === 'seo' ? 'border-orange-600 text-orange-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>SEO & Open Graph</button>
                </div>
              </div>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto">
              {activeTab === 'content' ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Title</label>
                        <input name="title" defaultValue={editingPost?.title} required className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2 text-lg font-bold" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Excerpt</label>
                        <textarea name="excerpt" defaultValue={editingPost?.excerpt} required className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" rows={2} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Image</label>
                          <input type="file" name="featured_image" accept="image/*" className="mt-1 block w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-gray-100 file:text-gray-700" required={!editingPost} />
                          {editingPost?.featured_image && (
                            <div className="mt-2 h-20 w-32 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                              <img src={getImageUrl(editingPost.featured_image) || undefined} alt="" className="h-full w-full object-cover" />
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 text-xs">Image Alt Text</label>
                          <input name="featured_image_alt" defaultValue={editingPost?.featured_image_alt} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2 text-sm" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 text-xs">Read Time (min)</label>
                          <input type="number" name="read_time" defaultValue={editingPost?.read_time || 5} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2 text-sm" min={1} />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Category</label>
                          <select name="category" defaultValue={editingPost?.category_id || editingPost?.category?.id || editingPost?.category} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2 text-sm" required>
                            <option value="">Select Category</option>
                            {categories.map(cat => (
                              <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Status</label>
                          <select name="status" defaultValue={editingPost?.status || 'draft'} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2 text-sm">
                            <option value="draft">Draft</option>
                            <option value="published">Published</option>
                          </select>
                        </div>
                        <div className="flex items-center">
                          <input type="checkbox" name="is_featured" defaultChecked={editingPost?.is_featured} className="h-5 w-5 text-orange-600 border-gray-300 rounded" />
                          <label className="ml-2 block text-sm text-gray-700 font-medium">Featured</label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                    <SafeJoditEditor
                      ref={editor}
                      value={content}
                      config={editorConfig}
                      onBlur={newContent => setContent(newContent)}
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center"><Search className="h-3 w-3 mr-1" /> General SEO</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Meta Title</label>
                      <input name="meta_title" defaultValue={editingPost?.meta_title} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" maxLength={60} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Meta Keywords</label>
                      <input name="meta_keywords" defaultValue={editingPost?.meta_keywords} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" placeholder="comma, separated" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Meta Description</label>
                      <textarea name="meta_description" defaultValue={editingPost?.meta_description} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" rows={3} maxLength={160} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Robots Meta</label>
                      <input name="robots_meta" defaultValue={editingPost?.robots_meta} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Canonical URL</label>
                      <input name="canonical_url" defaultValue={editingPost?.canonical_url} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center"><Globe className="h-3 w-3 mr-1" /> Social OpenGraph</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">OG Title</label>
                      <input name="og_title" defaultValue={editingPost?.og_title} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">OG Description</label>
                      <textarea name="og_description" defaultValue={editingPost?.og_description} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" rows={3} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 text-xs">OG Image (File)</label>
                      <input type="file" name="og_image" accept="image/*" className="mt-1 block w-full text-xs text-gray-500 file:border file:bg-gray-50 file:rounded" />
                      {editingPost?.og_image && (
                        <div className="mt-2 h-16 w-32 rounded overflow-hidden bg-gray-100">
                          <img src={getImageUrl(editingPost.og_image) || undefined} alt="" className="h-full w-full object-cover" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center"><Tag className="h-3 w-3 mr-1" /> SEO Keywords & Extras</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Focus Keyword</label>
                      <input name="focus_keyword" defaultValue={editingPost?.focus_keyword} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Breadcrumbs Text</label>
                      <input name="breadcrumbs" defaultValue={editingPost?.breadcrumbs} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Schema Markup (JSON)</label>
                      <textarea name="schema_markup" defaultValue={typeof editingPost?.schema_markup === 'object' ? JSON.stringify(editingPost.schema_markup, null, 2) : editingPost?.schema_markup} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2 font-mono text-xs" rows={4} />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-100">
                <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2 text-gray-600 font-medium hover:text-gray-800 transition">Cancel</button>
                <button type="submit" className="px-8 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition">
                  {editingPost ? 'Update Post' : 'Publish Article'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Article</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Metadata</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Status</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {posts.map((post) => (
                <tr key={post.id} className="hover:bg-gray-50/50 transition">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="h-10 w-16 rounded overflow-hidden bg-gray-100 mr-4">
                        {post.featured_image && <img src={getImageUrl(post.featured_image) || undefined} alt="" className="h-full w-full object-cover" />}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-gray-900 line-clamp-1">{post.title}</div>
                        <div className="text-xs text-gray-400 flex items-center mt-0.5">
                          <Calendar className="h-3 w-3 mr-1" /> {new Date(post.published_at || Date.now()).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs text-gray-600 font-medium flex items-center">
                      <Tag className="h-3 w-3 mr-1 text-orange-400" /> {post.category_name}
                    </div>
                    <div className="text-[10px] text-gray-400 flex items-center mt-1">
                      <UserIcon className="h-3 w-3 mr-1" /> {post.author_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded ${post.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {post.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleStatusToggle(post)}
                        className={`p-2 rounded-lg transition ${post.status === 'published' ? 'text-green-500 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                        title={post.status === 'published' ? 'Unpublish' : 'Publish'}
                      >
                        <Globe className="h-4 w-4" />
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const detail = await apiService.getBlogPostDetail(post.slug);
                            setEditingPost(detail);
                            setContent(detail.content || detail.body || '');
                            setActiveTab('content');
                            setShowForm(true);
                          } catch (err) { console.error(err); }
                        }}
                        className="p-2 text-gray-400 hover:text-orange-600 transition"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(post.slug)} className="p-2 text-gray-400 hover:text-red-600 transition">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div >
  );
};

export default Blog;
